import Typography from '@mui/material/Typography';
import React, { forwardRef, useMemo, useRef } from 'react';
import { ChevronLeft, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { ButtonVariants } from 'shared/components/ui/popup-menu/types';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import * as Styled from './custom-form-select.style';
import { DisplayTypes, Option, Size } from './types';

const defaultRenderOption: React.FC<{ value: Option }> = ({ value }) =>
  value.label;

export type CustomFormSelectProps = {
  id?: ID;
  options: Option[];
  value?: string;
  placeholder?: string;
  selectedLabel?: string;
  interActionOption?: Option;
  onInteractionOption?: () => void;
  onChange?: (value?: string) => void;
  startIconColor?: Colors;
  endColor?: Colors;
  canClear?: boolean;
  renderValue?: React.FC<{ value: Option }>;
  noBorders?: boolean;
  size?: Size;
  inheritColor?: boolean;
  noOverflow?: boolean;
};

export const CustomFormSelect = forwardRef<
  HTMLDivElement,
  CustomFormSelectProps
>(
  (
    {
      id,
      options,
      value,
      placeholder,
      selectedLabel,
      onChange,
      interActionOption,
      onInteractionOption,
      startIconColor,
      endColor,
      canClear,
      renderValue: RenderValue = defaultRenderOption,
      noBorders,
      size = Size.Medium,
      inheritColor,
      noOverflow,
    },
    ref,
  ) => {
    const containerRef = useForwardedRef<HTMLDivElement>(ref);
    const menuRef = useRef<HTMLDivElement>(null);
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();

    const optionsLists = useMemo(
      () =>
        options.reduce<Option[][]>(
          (acc, option) => {
            acc[acc.length - 1].push(option);

            if (option.hasBottomSeparator) {
              acc.push([]);
            }

            return acc;
          },
          [[]],
        ),
      [options],
    );

    const _onChange = (e: React.MouseEvent) => {
      const id = e.currentTarget?.getAttribute('data-value');
      if (id) {
        onChange?.(id);
      }

      closeMenu();
    };

    const onClear = (e: React.MouseEvent) => {
      e.preventDefault();
      onChange?.(undefined);
      closeMenu();
    };

    const selectedOption = useMemo(
      () => options.find((option) => option.value === value),
      [options, value],
    );
    const hasSelectedValue = !!selectedOption || !!selectedLabel;

    return (
      <Styled.InputContainer ref={containerRef}>
        <Styled.Button
          $noBorders={!!noBorders}
          $size={size}
          $inheritColor={!!inheritColor}
          $isPlaceholder={!hasSelectedValue}
          $noOverflow={!!noOverflow}
          onClick={openMenu}
          type="button"
          id={id}
        >
          {selectedLabel ??
            (selectedOption ? (
              <RenderValue value={selectedOption} />
            ) : (
              placeholder
            ))}
        </Styled.Button>

        <Styled.InteractionContainer
          $inheritColor={!!inheritColor}
          $size={size}
        >
          {hasSelectedValue && canClear ? (
            <Styled.ClearButton onClick={onClear} type="button" tabIndex={-1}>
              <Icon icon={X} />
            </Styled.ClearButton>
          ) : (
            <Styled.IconContainer onClick={openMenu}>
              <Icon icon={ChevronLeft} />
            </Styled.IconContainer>
          )}
        </Styled.InteractionContainer>

        {menuOpen && (
          <PopupMenu
            ref={menuRef}
            referenceElement={containerRef}
            onOutside={closeMenu}
            position="bottom"
            fallbackPositions={[
              'top',
              'right-end',
              'right-start',
              'bottom-end',
              'bottom-start',
              'left-start',
              'left-end',
              'top-end',
              'top-start',
            ]}
            hasParentWidth
          >
            {optionsLists.map((optionsList, index) => (
              <PopupMenuList
                key={`options-${index}`}
                hasBottomBorder={index !== optionsLists.length - 1}
              >
                {optionsList.map((option) => (
                  <PopupMenuListItem key={option.value}>
                    <PopupMenuButton
                      start={option.start}
                      startColor={startIconColor}
                      onClick={_onChange}
                      data-value={option.value}
                      end={
                        option.description ? (
                          <Typography variant="overline">
                            {option.description}
                          </Typography>
                        ) : undefined
                      }
                      endColor={endColor}
                      variant={
                        option.displayType === DisplayTypes.ErrorButton
                          ? ButtonVariants.Error
                          : undefined
                      }
                    >
                      {option.label}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                ))}
              </PopupMenuList>
            ))}

            {!!interActionOption && (
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuButton
                    start={interActionOption.start}
                    end={
                      <Typography variant="overline">
                        {interActionOption.description}
                      </Typography>
                    }
                    onClick={onInteractionOption}
                  >
                    {interActionOption.label}
                  </PopupMenuButton>
                </PopupMenuListItem>
              </PopupMenuList>
            )}
          </PopupMenu>
        )}
      </Styled.InputContainer>
    );
  },
);
